import {
  BLOCKING_MESSAGE,
  CAMPAIGN_STATUS_BADGE_PROPS,
  useUpdateCampaign,
  PendingChangesIcon,
  useGetContractorCampaignPausedReasons,
} from 'entities/campaign';
import type { ContractorGetCampaignModel } from 'entities/campaign';
import { Badge } from 'shared/ui/Badge';
import { MutatableSwitcher, getRunCampaignError } from 'shared/ui/Switcher';
import { Tooltip } from 'shared/ui/Tooltip';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { FormSelect } from 'shared/ui/Select';

import { getCampaignPausedReasonsOptions } from './get-paused-reasons-options';
import { ContractorCampaignDetailsAlertBoxes } from './ContractorCampaignDetailsAlertBoxes';
import { usePendingRunStatus } from './usePendingRunStatus';

interface ContractorCampaignDetailsProps {
  campaign: ContractorGetCampaignModel;
  isBlockingTime: boolean;
}

export function ContractorCampaignDetails({ campaign, isBlockingTime }: ContractorCampaignDetailsProps) {
  const useUpdateFn = () => useUpdateCampaign(campaign.id);
  const campaignPausedReasonsQuery = useGetContractorCampaignPausedReasons(campaign.id);
  const campaignPausedReasonsData = campaignPausedReasonsQuery.data?.data;
  const campaignPausedReasonsOptions = getCampaignPausedReasonsOptions(campaignPausedReasonsData);
  const showPausedReasonsSelect = !campaign.isActive && campaignPausedReasonsOptions;
  const campaignPausedReasonId = campaign.pausedReasonId?.toString() || '';
  const { isPendingIcon, tooltipText } = usePendingRunStatus(campaign);

  return (
    <div className="border-b border-slate-100 pb-8">
      <div className="flex items-center justify-between">
        <div className="text-sm">{campaign.name}</div>
        <Badge {...CAMPAIGN_STATUS_BADGE_PROPS[campaign.status]} />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex">
          <div className="text-sm">Run campaign</div>
          {isPendingIcon && (
            <PendingChangesIcon
              className="ml-2 mt-[-2px]"
              blockingMessage={tooltipText}
              appliesAt={campaign.pendingChanges?.appliesAt ?? null}
            />
          )}
        </div>
        <Tooltip
          isDisabled={!isBlockingTime}
          placement="bottom-end"
          content={BLOCKING_MESSAGE}
        >
          <MutatableSwitcher
            isDisabled={isBlockingTime}
            useUpdate={useUpdateFn}
            buildMutateArgs={(isActive) => ({ payload: { isActive } })}
            getErrorMessage={getRunCampaignError}
            name="isActive"
            isChecked={campaign.isActive}
            {...getDataAutoTestAttributes(['switcher-run-campaign'])}
          />
        </Tooltip>
      </div>
      <ContractorCampaignDetailsAlertBoxes campaign={campaign} />
      {showPausedReasonsSelect && (
        <FormSelect
          title="Pausing reason"
          name="pausedReason"
          wrapperClass="mt-4"
          options={campaignPausedReasonsOptions}
          isCancel
          selectedOptionId={campaignPausedReasonId}
          {...getDataAutoTestAttributes(['select-campaign-pausing-reason'])}
        />
      )}
    </div>
  );
}
